import React from 'react';

import { ErrorIcon } from '~/components/icons';
import { useLoadingSpan } from '~/observability/useLoadingSpan';
import cn from '~/Utils/cn';

export interface SkeletonTableProps {
  rowsCount: number;
  columnsCount: number;
  maxHeight?: string;
  isError?: boolean;
  cellClassName?: string;
  wrapperPadding?: string | number;
  /**
   * Optional override for the auto-derived Sentry span name. When omitted, the
   * babel-loading-source plugin injects a `__loadingSource`-derived fallback. See
   * NGTPA-24705.
   */
  loadingSpanName?: string;
  /** Auto-injected by babel-loading-source. Not for callers to pass. */
  __loadingSource?: { fileName: string; lineNumber: number };
}

const SkeletonTableCell: React.FC<{ cellClassName?: string }> = ({ cellClassName }) => (
  <div className={cn('flex h-20 w-full animate-pulse px-12', cellClassName)}>
    <div className="h-full w-full rounded-lg bg-slate-400" />
  </div>
);

const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rowsCount,
  columnsCount,
  maxHeight,
  isError = false,
  cellClassName,
  wrapperPadding = 16,
  loadingSpanName,
  __loadingSource,
}) => {
  // SkeletonTable is mounted only while loading; the span tracks its visible lifetime.
  // The hook treats isError as terminal — when it flips true, the span ends tagged 'error'.
  useLoadingSpan({ isLoading: true, isError, loadingSpanName, __loadingSource });
  const height = maxHeight ? `h-[${maxHeight}]` : 'h-full';

  if (isError) {
    return <ErrorIcon size={30} />;
  }

  return (
    <div
      className={`w-full p-[var(--skeleton-wrapper-padding)] ${height}`}
      data-testid="skeleton-table"
      style={{
        '--skeleton-wrapper-padding': typeof wrapperPadding === 'number' ? `${wrapperPadding}px` : wrapperPadding,
      }}
    >
      <div className="flex w-full justify-around p-16">
        <SkeletonTableCell cellClassName={cellClassName} />
      </div>
      {Array.from({ length: rowsCount }, (_, index) => (
        <div key={index} className="flex w-full justify-around p-16">
          {Array.from({ length: columnsCount }, (_, columnIndex) => (
            <SkeletonTableCell key={columnIndex} cellClassName={cellClassName} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default SkeletonTable;
